import { useState, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Upload, Trash2, Download, Video as VideoIcon, AlertCircle } from "lucide-react";
import { useDateFormat } from '@/contexts/DateFormatContext';
import { toast } from "sonner";
import { useTranslation } from "../i18n";

// Optimized settings for large file uploads (3GB+)
const CHUNK_SIZE = 100 * 1024 * 1024; // 100MB chunks (Cloudflare recommended for large files)
const MAX_FILE_SIZE = 15 * 1024 * 1024 * 1024; // 15GB
const MAX_RETRIES = 3; // Retry failed chunks up to 3 times
const CHUNK_TIMEOUT = 600000; // 10 minutes timeout per chunk for large chunks
const PARALLEL_UPLOADS = 6; // Upload 6 chunks in parallel (Cloudflare recommended)
const PRESIGNED_URL_BATCH_SIZE = 10; // Request presigned URLs in batches

interface VideoUploadState {
  id: string;
  file: File;
  sessionId?: string;
  uploadId?: string;
  storageKey?: string;
  parts: Array<{ PartNumber: number; ETag: string }>;
  uploadedChunks: number;
  totalChunks: number;
  progress: number;
  status: "pending" | "uploading" | "completing" | "completed" | "error";
  error?: string;
}

export default function ProjectVideos() {
  const { t } = useTranslation();
  const params = useParams();
  const projectId = parseInt(params.id || "0");
  const [, navigate] = useLocation();
  const { formatDateTime } = useDateFormat();

  const [uploads, setUploads] = useState<VideoUploadState[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: project } = trpc.projects.getById.useQuery({ id: projectId });
  const { data: videos, refetch } = trpc.videos.list.useQuery({ projectId });
  const utils = trpc.useUtils();

  const deleteVideoMutation = trpc.videos.delete.useMutation({
    onSuccess: () => {
      refetch();
      setDeleteDialogOpen(false);
      toast.success(t.videos.videoDeleted);
    },
  });

  const startUploadMutation = trpc.videos.startUpload.useMutation();
  const getPresignedUrlsMutation = trpc.videos.getPresignedUrls.useMutation();
  const registerPartMutation = trpc.videos.registerPart.useMutation();
  const completeUploadMutation = trpc.videos.completeUpload.useMutation();
  const abortUploadMutation = trpc.videos.abortUpload.useMutation();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: ${t.videos.maxFileSize}`);
        continue;
      }

      // Validate type
      if (!file.type.startsWith("video/")) {
        toast.error(`${file.name}: ${t.videos.error}`);
        continue;
      }

      const uploadId = `${Date.now()}-${Math.random()}`;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      setUploads((prev) => [...prev, {
        id: uploadId,
        file,
        uploadedChunks: 0,
        totalChunks,
        progress: 0,
        status: "pending",
        parts: [], // Initialize empty parts array
      }]);

      // Start upload process
      startUpload(uploadId, file, totalChunks);
    }

    // Reset input
    e.target.value = "";
  };

  const startUpload = useCallback(async (uploadId: string, file: File, totalChunks: number) => {
    console.log(`[Video Upload] Starting upload for ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB) in ${totalChunks} chunks`);
    try {
      // Update status
      setUploads((prev) => prev.map(upload =>
        upload.id === uploadId
          ? { ...upload, status: "uploading" as const }
          : upload
      ));

      // Start multipart upload session
      console.log(`[Video Upload] Initiating multipart upload session...`);
      const { sessionId, uploadId: s3UploadId, key } = await startUploadMutation.mutateAsync({
        projectId,
        filename: file.name,
        mimeType: file.type,
        fileSize: file.size,
      });

      console.log(`[Video Upload] Session created: ${sessionId}`);
      
      // Update with session info
      setUploads((prev) => prev.map(upload =>
        upload.id === uploadId
          ? { ...upload, sessionId, uploadId: s3UploadId, storageKey: key }
          : upload
      ));

      // Upload chunks using presigned URLs with parallel uploads
      console.log(`[Video Upload] Starting optimized upload (${totalChunks} chunks, ${PARALLEL_UPLOADS} parallel)...`);
      
      // Collect all uploaded parts
      const allParts: Array<{ PartNumber: number; ETag: string }> = [];
      let completedChunks = 0;
      
      // Upload chunks in parallel batches
      for (let batchStart = 0; batchStart < totalChunks; batchStart += PARALLEL_UPLOADS) {
        const batchEnd = Math.min(batchStart + PARALLEL_UPLOADS, totalChunks);
        const batchPartNumbers = Array.from(
          { length: batchEnd - batchStart },
          (_, i) => batchStart + i + 1
        );

        // Get presigned URLs for this batch
        console.log(`[Video Upload] Requesting presigned URLs for parts ${batchPartNumbers[0]}-${batchPartNumbers[batchPartNumbers.length - 1]}`);
        const { urls } = await getPresignedUrlsMutation.mutateAsync({
          sessionId,
          partNumbers: batchPartNumbers,
        });

        // Upload chunks in parallel
        const uploadPromises = urls.map(async ({ partNumber, url }) => {
          const chunkIndex = partNumber - 1;
          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);

          let retries = 0;
          let uploaded = false;
          let etag: string | null = null;

          while (!uploaded && retries < MAX_RETRIES) {
            try {
              console.log(`[Video Upload] Uploading part ${partNumber} (${(chunk.size / 1024 / 1024).toFixed(2)}MB) directly to R2...`);
              
              // Upload directly to R2 using presigned URL
              const uploadResponse = await Promise.race([
                fetch(url, {
                  method: 'PUT',
                  body: chunk,
                  headers: {
                    'Content-Type': 'application/octet-stream',
                  },
                }),
                new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('Upload timeout')), CHUNK_TIMEOUT)
                )
              ]);

              if (!uploadResponse.ok) {
                throw new Error(`Upload failed with status ${uploadResponse.status}`);
              }

              // Get ETag from response
              etag = uploadResponse.headers.get('ETag');
              if (!etag) {
                throw new Error('No ETag returned from R2');
              }

              // Remove quotes from ETag if present
              etag = etag.replace(/"/g, '');

              // Register the uploaded part
              await registerPartMutation.mutateAsync({
                sessionId,
                partNumber,
                etag,
              });

              uploaded = true;

              // Update progress based on completed chunks count
              completedChunks++;
              const progress = (completedChunks / totalChunks) * 100;
              console.log(`[Video Upload] Part ${partNumber}/${totalChunks} uploaded successfully (${completedChunks} completed, ${progress.toFixed(1)}%)`);
              
              setUploads((prev) => prev.map(upload =>
                upload.id === uploadId
                  ? { ...upload, uploadedChunks: completedChunks, progress }
                  : upload
              ));

            } catch (error) {
              retries++;
              console.error(`[Video Upload] Part ${partNumber} failed (attempt ${retries}/${MAX_RETRIES}):`, error);
              
              if (retries >= MAX_RETRIES) {
                throw new Error(`Failed to upload part ${partNumber} after ${MAX_RETRIES} attempts`);
              }
              
              // Wait before retry (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, Math.min(2000 * Math.pow(2, retries), 30000)));
            }
          }

          return { PartNumber: partNumber, ETag: etag! };
        });

        // Wait for all uploads in this batch to complete and collect parts
        const batchParts = await Promise.all(uploadPromises);
        allParts.push(...batchParts);
        console.log(`[Video Upload] Batch ${batchStart / PARALLEL_UPLOADS + 1} completed (${completedChunks}/${totalChunks} total)`);
      }

      // Complete upload
      console.log(`[Video Upload] All chunks uploaded. Completing upload...`);
      setUploads((prev) => prev.map(upload =>
        upload.id === uploadId
          ? { ...upload, status: "completing" as const }
          : upload
      ));

      // Sort parts by part number to ensure correct order
      allParts.sort((a, b) => a.PartNumber - b.PartNumber);
      
      console.log(`[Video Upload] Completing with ${allParts.length} parts`);
      console.log(`[Video Upload] First part:`, allParts[0]);
      console.log(`[Video Upload] Last part:`, allParts[allParts.length - 1]);
      
      await completeUploadMutation.mutateAsync({
        sessionId,
        projectId,
        filename: file.name,
        fileSize: file.size,
        mimeType: file.type,
        uploadId: s3UploadId,
        parts: allParts,
      });

      // Mark as completed
      console.log(`[Video Upload] Upload completed successfully!`);
      setUploads((prev) => prev.map(upload =>
        upload.id === uploadId
          ? { ...upload, status: "completed" as const, progress: 100 }
          : upload
      ));

      toast.success(`${file.name} ${t.videos.completed}`);
      refetch();

      // Remove from uploads after 3 seconds
      setTimeout(() => {
        setUploads((prev) => prev.filter(upload => upload.id !== uploadId));
      }, 3000);
    } catch (error) {
      console.error("[Video Upload] Upload failed with error:", error);
      console.error("[Video Upload] Error details:", error instanceof Error ? error.message : String(error));
      setUploads((prev) => prev.map(upload =>
        upload.id === uploadId
          ? {
              ...upload,
              status: "error" as const,
              error: error instanceof Error ? error.message : t.videos.uploadFailed
            }
          : upload
      ));

      // Try to abort
      const upload = uploads.find(u => u.id === uploadId);
      if (upload?.sessionId) {
        try {
          await abortUploadMutation.mutateAsync({ sessionId: upload.sessionId });
        } catch (abortError) {
          console.error("Failed to abort upload:", abortError);
        }
      }

      toast.error(`${file.name}: ${t.videos.uploadFailed}`);
    }
  }, [projectId, startUploadMutation, getPresignedUrlsMutation, registerPartMutation, completeUploadMutation, abortUploadMutation, refetch, t]);

  const handleDelete = () => {
    if (!selectedVideo) return;
    deleteVideoMutation.mutate({ videoId: selectedVideo.id });
  };

  const handleDownload = async (video: any) => {
    const toastId = toast.loading(t.videos.preparingDownload || "Download wird vorbereitet...");
    try {
      const res = await utils.videos.getDownloadUrl.fetch({ videoId: video.id });
      toast.success(t.videos.downloadReady || "Download bereit!", { id: toastId });
      window.open(res.url, "_blank");
    } catch (error) {
      toast.error(t.videos.downloadLinkError, { id: toastId });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
  };

  const formatDate = (date: Date | string) => formatDateTime(date instanceof Date ? date.toISOString() : date);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate("/projects")}
            className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t.videos.backToProject}
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{project?.title}</h1>
            <p className="text-muted-foreground">{t.videos.title}</p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t.videos.uploadVideos}</CardTitle>
          <CardDescription>
            {t.videos.maxFileSize}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label>
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="video-upload"
              />
              <Button asChild>
                <label htmlFor="video-upload" className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  {t.videos.selectFiles}
                </label>
              </Button>
            </label>

            {/* Active Uploads */}
            {uploads.map((upload) => (
              <div key={upload.id} className="space-y-2 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{upload.file.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {formatFileSize(upload.file.size)}
                  </span>
                </div>
                <Progress value={upload.progress} />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {upload.status === "pending" && t.videos.pending}
                    {upload.status === "uploading" &&
                      `${t.videos.uploading} ${upload.uploadedChunks}/${upload.totalChunks}`}
                    {upload.status === "completing" && t.common.loading}
                    {upload.status === "completed" && t.videos.completed}
                    {upload.status === "error" && (
                      <span className="text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {upload.error}
                      </span>
                    )}
                  </span>
                  <span>{upload.progress.toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Videos List */}
      <Card>
        <CardHeader>
          <CardTitle>{t.videos.title} ({videos?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {!videos || videos.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <VideoIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t.videos.noVideos}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <VideoIcon className="h-8 w-8 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">{video.filename}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatFileSize(video.fileSize)} • {formatDate(video.uploadedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(video)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedVideo(video);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.videos.deleteVideo}</DialogTitle>
            <DialogDescription>
              {t.videos.confirmDelete}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteVideoMutation.isPending}
            >
              {t.common.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
